import React, { useState } from "react";
import { NoirBg } from "@/components/NoirBg";
import { UploadWorkspace } from "@/components/UploadWorkspace";
import { UserJobs } from "@/components/UserJobs";
import { UserCredits } from "@/components/UserCredits";
import { UserReferrals } from "@/components/UserReferrals";
import { UserMarketWallet } from "@/components/UserMarketWallet";
import { UserAccount } from "@/components/UserAccount";
import { UserPrivacy } from "@/components/UserPrivacy";
import { UserSecurity } from "@/components/UserSecurity";
import { useUserAuth } from "@/context/UserAuthContext";
import { userApi } from "@/lib/userApi";
import { UploadCloud, ListChecks, Coins, UserRound, MessagesSquare, EyeOff, Lock, Newspaper, Store, Gift, Tag, Banknote, Copy, Radar } from "lucide-react";
import { Link } from "react-router-dom";

const TABS = [
  { key: "subir", label: "Crear", icon: UploadCloud },
  { key: "trabajos", label: "Mis trabajos", icon: ListChecks },
  { key: "creditos", label: "Créditos", icon: Coins },
  { key: "invita", label: "Invita y gana", icon: Gift },
  { key: "mercado", label: "Ventas y cobros", icon: Banknote },
  { key: "cuenta", label: "Mi cuenta", icon: UserRound },
  { key: "privacidad", label: "Privacidad", icon: EyeOff },
  { key: "seguridad", label: "Seguridad", icon: Lock },
];

export default function UserPanel() {
  const [tab, setTab] = useState("subir");
  const { profile, refresh } = useUserAuth();

  return (
    <div className="min-h-screen noir-bg theme-noir" style={{ backgroundColor: "#050507" }}>
      <NoirBg />

      <header className="sticky top-0 z-30 border-b border-white/10 backdrop-blur-xl"
        style={{ background: "rgba(10,3,4,0.55)" }}>
        <div className="max-w-5xl mx-auto px-5 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src="/logo-digital-forge.jpg" alt="Digital Forge" data-testid="brand-logo"
              className="w-9 h-9 rounded-md object-cover shrink-0 border border-[#FF8A2B]/40"
              style={{ boxShadow: "0 0 14px rgba(255,138,43,0.28)" }} />
            <div className="leading-tight">
              <div className="font-heading font-bold text-sm text-white">Editor y mi cuenta</div>
              <div className="text-[10px] text-white/45 font-mono truncate max-w-[180px]">{profile?.email}</div>
            </div>
          </div>
          <div className="flex items-center gap-2">
          <Link to="/muro" data-testid="panel-to-feed"
            className="glass-btn glass-btn-red px-3 py-2 rounded-xl text-xs flex items-center gap-1.5">
            <Newspaper className="w-3.5 h-3.5" /> Volver al muro
          </Link>
          <Link to="/mercado" data-testid="panel-to-market"
            className="glass-btn px-3 py-2 rounded-xl text-xs flex items-center gap-1.5">
            <Store className="w-3.5 h-3.5" /> Mercado
          </Link>
          <Link to="/clonador" data-testid="panel-to-cloner"
            className="glass-btn px-3 py-2 rounded-xl text-xs flex items-center gap-1.5">
            <Copy className="w-3.5 h-3.5" /> Clonador
          </Link>
          <Link to="/matrix" data-testid="panel-to-matrix"
            className="glass-btn px-3 py-2 rounded-xl text-xs flex items-center gap-1.5 text-red-300">
            <Radar className="w-3.5 h-3.5" /> Matrix
          </Link>
          <Link to="/precios" data-testid="panel-to-pricing"
            className="glass-btn px-3 py-2 rounded-xl text-xs flex items-center gap-1.5">
            <Tag className="w-3.5 h-3.5" /> Precios
          </Link>
          <Link to="/comunidad" data-testid="panel-to-community"
            className="glass-btn px-3 py-2 rounded-xl text-xs flex items-center gap-1.5">
            <MessagesSquare className="w-3.5 h-3.5" /> Chats
          </Link>
          <div className="glass-card px-4 py-2 text-right" data-testid="panel-credits">
            <div className="text-[10px] uppercase tracking-[0.16em] text-white/50">Créditos</div>
            <div className="text-lg font-bold text-white leading-none">{profile?.credits ?? 0}</div>
          </div>
          </div>
        </div>
        <div className="max-w-5xl mx-auto px-5 flex gap-1 overflow-x-auto">
          {TABS.map((t) => (
            <button key={t.key} data-testid={`panel-tab-${t.key}`} onClick={() => setTab(t.key)}
              className={`flex items-center gap-2 px-4 py-2.5 text-sm rounded-t-lg transition-colors ${
                tab === t.key ? "text-white bg-white/8 border-b-2 border-red-500" : "text-white/55 hover:text-white"
              }`}>
              <t.icon className="w-4 h-4" /> {t.label}
            </button>
          ))}
        </div>
      </header>

      <main className="relative z-10 max-w-5xl mx-auto px-5 py-8">
        {tab === "subir" && (
          <>
            <h1 className="font-heading text-2xl font-bold text-white mb-1">Sube tu imagen o PDF</h1>
            <p className="text-sm text-white/55 mb-6">
              Reconstruye en capas, edita en el lienzo o dile a la IA qué quieres cambiar.
            </p>
            <UploadWorkspace client={userApi} tokenKey="user_token" jobsPath="/panel"
              onDone={refresh} />
          </>
        )}
        {tab === "trabajos" && <UserJobs />}
        {tab === "creditos" && <UserCredits />}
        {tab === "invita" && <UserReferrals />}
        {tab === "mercado" && <UserMarketWallet />}
        {tab === "cuenta" && <UserAccount />}
        {tab === "privacidad" && <UserPrivacy />}
        {tab === "seguridad" && <UserSecurity />}
      </main>
    </div>
  );
}
